import React, { useEffect, useState } from 'react'
import { Button, Checkbox, Datepicker, Input, Select, Textarea } from '@mobiscroll/react-latest'
import moment from 'moment'
import { MAX_PASSENGER_COUNT } from 'src/components/riders/rider-portal/trips/create/CreateTrip'
import Card from 'src/components/riders/rider-portal/common/Card'
import Label from 'src/components/riders/rider-portal/common/Label'
import Skeleton from 'src/components/riders/rider-portal/common/Skeleton'
import Calendar3 from 'src/components/icons/ui/Calendar3'
import Clock from 'src/components/icons/ui/Clock'
import MapPin from 'src/components/icons/ui/MapPin'
import { useBreakpoint } from 'src/components/riders/rider-portal/hooks'
import { IPortalProduct, IService } from 'src/types'
import { IPortalPassenger } from 'src/types/rider-portal'
import { ILocationV2 } from 'src/types/location'
import { PORTAL_CALENDAR_PROPS, PORTAL_INPUT_PROPS, RIDER_PORTAL_UI_MESSAGES } from 'src/constants/rider-portal'

const DEFAULT_SELECT_DATA = [{ text: '', value: '' }]
interface ITripDetailsProps {
  accessibilities: string[]
  availableProducts: IPortalProduct[]
  bookingType: 'on-demand' | 'schedule'
  dateTime: {
    date: Date | string
    time: Date | string
  }
  dropOffLocation: ILocationV2 | null
  fetchingProducts: boolean
  selectedProduct: IPortalProduct | null
  selectedService: IService | null
  specialInstructions: string
  tripRequestRiders: IPortalPassenger[]
  handleChangeAddress: (fullAddress: string) => void
  handleDateTimeChange: (type: 'date' | 'time', value: Date) => void
  handleMapLabelClick: () => void
  handleSelectAccessibilities: (checked: boolean, value: string) => void
  handleSelectProduct: (product: IPortalProduct) => void
  handleShowPaymentSourceModal: () => void
  handleSpecialInstructionsChange: (value: string) => void
  handleTripRiders: (passenger: IPortalPassenger) => void
  handleUpdatePassengerCount: (item: { type: string; index: number }) => void
}

const TripDetails = ({
  accessibilities,
  availableProducts,
  bookingType,
  dateTime,
  dropOffLocation,
  fetchingProducts,
  selectedProduct,
  selectedService,
  specialInstructions,
  tripRequestRiders,
  handleChangeAddress,
  handleDateTimeChange,
  handleMapLabelClick,
  handleSelectAccessibilities,
  handleSelectProduct,
  handleShowPaymentSourceModal,
  handleSpecialInstructionsChange,
  handleTripRiders,
  handleUpdatePassengerCount
}: ITripDetailsProps) => {
  const [allowedPassengerCount, setAllowedPassengerCount] = useState(MAX_PASSENGER_COUNT)
  const [passenger, setPassenger] = useState({
    fareType: '',
    riderType: '',
    totalRider: 0
  })

  useEffect(() => {
    let totalPassengerCount = 0
    tripRequestRiders.forEach(({ totalRider }) => {
      totalPassengerCount += totalRider
    })
    const remainingPassengerCount =
      MAX_PASSENGER_COUNT - totalPassengerCount < 1 ? 0 : MAX_PASSENGER_COUNT - totalPassengerCount

    setPassenger({ fareType: '', riderType: '', totalRider: 0 })
    setAllowedPassengerCount(remainingPassengerCount)
  }, [tripRequestRiders])

  const { passengerTypes = [], paymentMethods = [] } = selectedService || {}
  const selectPassengerTypeData = passengerTypes.length
    ? passengerTypes
        .filter(({ isActive }) => isActive === true)
        .map(({ riderType }) => ({ text: riderType, value: riderType }))
        .sort((a, b) => a.text.localeCompare(b.text))
    : DEFAULT_SELECT_DATA

  const selectPaymentMethodData = paymentMethods.length
    ? paymentMethods
        .filter(({ activated }) => activated === true)
        .map(({ paymentName }) => ({ text: paymentName, value: paymentName }))
        .sort((a, b) => a.text.localeCompare(b.text))
    : DEFAULT_SELECT_DATA

  const { common, dataFeedback, footer, mobility, placeholder, trips } = RIDER_PORTAL_UI_MESSAGES
  const isMobile = useBreakpoint() === 'sm'

  const ACCESSIBILITY_MAPPING = [
    { label: '\u{1F91D} Hand-to-Hand', value: `\u{1F91D} Hand-to-Hand` },
    { label: '\u{1F9AE} Service-Animal', value: `\u{1F9AE} Service-Animal` },
    { label: '\u{1F9D1}\u{200D}\u{1F9AF} Blind-Person', value: `\u{1F9D1}\u{200D}\u{1F9AF} Blind-Person` },
    { label: '\u{1F9BB} Hearing-Impaired', value: '\u{1F9BB} Hearing-Impaired' },
    { label: '\u{1F4DE} Call(upon-arrival)', value: '\u{1F4DE} Call(upon-arrival)' },
    { label: '\u{1F9CF}\u{1F919} Cannot-Hear', value: '\u{1F9CF}\u{1F919} Cannot-hear' },
    { label: '\u{1F697}\u{1F9BD} Putting-Walker-in-trunk', value: '\u{1F697}\u{1F9BD} Putting-Walker-in-trunk' },
    { label: '\u{1F697}\u{1F6E3} Car-close-to-curb', value: '\u{1F697}\u{1F6E3} Car-close-to-curb' },
    { label: '\u{1F697} Car-by-curb-cut/no-curb', value: '\u{1F697} Car-by-curb-cut/no-curb' }
  ]

  const handlePassengerData = ({ name, value }: { name: string; value: string }) => {
    setPassenger(prev => ({
      ...prev,
      [name]: name === 'totalRider' ? Number(value) : value
    }))
  }

  return (
    <>
      <div className='rp-mb24'>
        <div className='text-center'>
          <Label htmlFor={trips.destination} required={true} text={trips.destination} />
        </div>
        <div className='flex items-center mt03 relative'>
          <Input
            {...PORTAL_INPUT_PROPS}
            className='rp-input-with-end-btn rp-input-with-start-icon w100'
            value={dropOffLocation && dropOffLocation.fullAddress ? dropOffLocation.fullAddress : ''}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChangeAddress(e.target.value)}
          />
          <MapPin className='absolute icon--18x18 rp-ml12 svg-color-red1' />
          <Label
            className='absolute cursor--pointer right-0 rp-map-modal-link rp-mr16'
            htmlFor={common.map}
            text={common.map}
            handleLabelClick={handleMapLabelClick}
          />
        </div>
      </div>

      {bookingType === 'schedule' && (
        <div className={`rp-mb24 ${isMobile ? 'block' : 'flex column-gap-16'}`}>
          <div className={`w100 ${isMobile ? 'rp-mb24' : ''}`}>
            <div className='text-center'>
              <Label htmlFor={common.date} required={true} text={common.date} />
            </div>
            <div className='mt03 relative'>
              <Datepicker
                {...PORTAL_CALENDAR_PROPS}
                controls={['calendar']}
                dateFormat='DD/MM/YYYY'
                inputProps={{
                  className: 'rp-input-with-start-icon w100',
                  readOnly: true
                }}
                min={moment()}
                placeholder='dd/mm/yyyy'
                value={dateTime.date ? moment(dateTime.date) : ''}
                onChange={({ value }) => handleDateTimeChange('date', value as Date)}
              />
              <Calendar3 className='absolute icon--18x18 rp-ml12 svg-color-black4 top-14' />
            </div>
          </div>
          <div className='w100'>
            <div className='text-center'>
              <Label htmlFor={common.time} required={true} text={common.time} />
            </div>
            <div className='mt03 relative'>
              <Datepicker
                {...PORTAL_CALENDAR_PROPS}
                controls={['time']}
                inputProps={{
                  className: 'rp-input-with-start-icon w100',
                  readOnly: true
                }}
                timeFormat='hh:mm a'
                placeholder='--:-- --'
                value={dateTime.time ? moment(dateTime.time) : ''}
                onChange={({ value }) => handleDateTimeChange('time', value as Date)}
              />
              <Clock className='absolute icon--18x18 rp-ml12 svg-color-black4 top-14' />
            </div>
          </div>
        </div>
      )}

      <div className='rp-mb24'>
        <div className='text-center'>
          <Label htmlFor={footer.products} required={true} text={footer.products} />
        </div>
        <div className='mt03 rp-products-wrapper'>
          {/* Initial feedback to user since dropOffLocation is a required data on the api */}
          {!dropOffLocation && (
            <Skeleton text={dataFeedback.pleaseSpecifyDestination} withBg={true} withLoading={false} />
          )}
          {dropOffLocation && !availableProducts.length && !fetchingProducts && (
            <Skeleton text={dataFeedback.noAvailableProducts} withBg={true} withLoading={false} />
          )}
          {fetchingProducts ? (
            <Skeleton text={dataFeedback.retrievingProducts} withBg={true} />
          ) : (
            <>
              {!!availableProducts.length &&
                dropOffLocation &&
                availableProducts.map(product => {
                  const { description = '', fare = 0, id = '', name = '' } = product || {}
                  if (id && name) {
                    return (
                      <div
                        className='rp-products-item'
                        key={id}
                        role='button'
                        tabIndex={0}
                        onClick={() => handleSelectProduct(product)}
                        onKeyDown={(e: React.KeyboardEvent<HTMLSpanElement>) => {
                          if (e.key === 'Enter') {
                            handleSelectProduct(product)
                          }
                        }}
                      >
                        <Card
                          className='border-boxf cursor--pointer h100'
                          active={selectedProduct ? selectedProduct.id === id : false}
                        >
                          <div
                            className={`${
                              isMobile ? 'block text-center' : 'flex items-center justify-between column-gap-16'
                            }`}
                          >
                            <h4 className='font-black1 font-medium my0 text-md'>{name}</h4>
                            <p className={`${isMobile ? 'mt03' : ''} font-black1 my0 text-sm`}>{`$${(
                              fare / 100
                            ).toFixed(2)}`}</p>
                          </div>
                          <p className='font-black1 mb0 mt03 text-sm'>
                            {description || <i>{dataFeedback.noDescriptionAvailable}</i>}
                          </p>
                        </Card>
                      </div>
                    )
                  }
                  return null
                })}
            </>
          )}
        </div>
      </div>

      {passengerTypes.length > 0 && paymentMethods.length > 0 && (
        <div className='rp-mb24'>
          <div className='text-center'>
            <Label htmlFor={trips.passengerDetails} text={trips.passengerDetails} />
          </div>
          <div className='rp-passenger-details mt03'>
            <div>
              <div className='rp-mb4'>
                <Label htmlFor={common.types} required={true} text={common.types} />
              </div>
              <Select
                {...PORTAL_INPUT_PROPS}
                className='rp-mt4'
                data={selectPassengerTypeData}
                disabled={allowedPassengerCount === 0}
                placeholder={
                  !!selectPassengerTypeData.length ? dataFeedback.pleaseSelectAnOption : dataFeedback.noOptionAvailable
                }
                touchUi={false}
                value={passenger.riderType}
                onChange={({ value }) => handlePassengerData({ name: 'riderType', value })}
              />
            </div>
            <div>
              <div className='rp-mb4'>
                <Label className='rp-mb4' htmlFor={trips.paymentMethods} required={true} text={trips.paymentMethods} />
              </div>
              <Select
                {...PORTAL_INPUT_PROPS}
                data={selectPaymentMethodData}
                disabled={allowedPassengerCount === 0}
                placeholder={
                  !!selectPaymentMethodData.length ? dataFeedback.pleaseSelectAnOption : dataFeedback.noOptionAvailable
                }
                touchUi={false}
                value={passenger.fareType}
                onChange={({ value }) => handlePassengerData({ name: 'fareType', value })}
              />
            </div>
            <div>
              <div className='rp-mb4'>
                <Label className='rp-mb4' htmlFor={trips.count} required={true} text={trips.count} />
              </div>
              <Input
                {...PORTAL_INPUT_PROPS}
                disabled={allowedPassengerCount === 0}
                inputClass='portal-number-input'
                max={allowedPassengerCount}
                min={0}
                type='number'
                value={`${passenger.totalRider}`}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  if (Number(e.target.value) <= allowedPassengerCount) {
                    handlePassengerData({ name: 'totalRider', value: e.target.value })
                  }
                }}
                onKeyDown={(e: React.KeyboardEvent<HTMLButtonElement>) => {
                  if (e.key === 'Enter' && (passenger.fareType && passenger.riderType && passenger.totalRider !== 0)) {
                    handleTripRiders(passenger)
                  }
                }}
              />
            </div>
            <div className='rp-portal-passenger self-end'>
              <Button
                className={`${isMobile ? 'rp-btn-continue w100' : 'rp-rounded-btn'}`}
                disabled={
                  !passenger.fareType || !passenger.riderType || !passenger.totalRider || allowedPassengerCount === 0
                }
                onClick={() => handleTripRiders(passenger)}
                onKeyPress={(e: React.KeyboardEvent<HTMLButtonElement>) => {
                  if (e.key === 'Enter') {
                    handleTripRiders(passenger)
                  }
                }}
              >
                <span className={`${isMobile ? 'rp-py4 text-sm' : 'fs-24'} font-white`}>
                  {isMobile ? trips.addPassenger : '+'}
                </span>
              </Button>
            </div>
          </div>
          {!!tripRequestRiders.length &&
            tripRequestRiders.map(({ fareType, riderType, totalRider }, index) => (
              <div className='rp-passenger-items rp-mt12' key={`${riderType}-${fareType}-${index}`}>
                <div className={`font-black1 rp-pl16 text-sm`}>{riderType}</div>
                <div
                  className={`font-black1 rp-pl16 text-sm ${fareType === 'Credit Card' ? 'cursor--pointer' : ''}`}
                  onClick={() => {
                    if (fareType === 'Credit Card') {
                      handleShowPaymentSourceModal()
                    }
                  }}
                >
                  {fareType}
                </div>
                <div className={``}>
                  <Button
                    className='passenger-count-btn'
                    tabIndex={0}
                    onClick={() => handleUpdatePassengerCount({ type: 'decrease', index })}
                    onKeyPress={(e: React.KeyboardEvent<HTMLButtonElement>) => {
                      if (e.key === 'Enter') {
                        handleUpdatePassengerCount({ type: 'decrease', index })
                      }
                    }}
                  >
                    {' - '}
                  </Button>
                  <span className='rp-px16 font-black1 text-sm'>{totalRider}</span>
                  <Button
                    className='passenger-count-btn'
                    disabled={allowedPassengerCount === 0}
                    tabIndex={0}
                    onClick={() => handleUpdatePassengerCount({ type: 'increase', index })}
                    onKeyPress={(e: React.KeyboardEvent<HTMLButtonElement>) => {
                      if (e.key === 'Enter') {
                        handleUpdatePassengerCount({ type: 'increase', index })
                      }
                    }}
                  >
                    {' + '}
                  </Button>
                </div>
              </div>
            ))}
        </div>
      )}

      <div className='rp-mb24'>
        <div className='text-center'>
          <Label htmlFor={trips.accessibilityAndSpecialNeeds} text={trips.accessibilityAndSpecialNeeds} />
        </div>
        <div className='rp-accessibility-container'>
          {ACCESSIBILITY_MAPPING.map(({ label, value }) => (
            <div className='rp-mt12' key={label}>
              <div className='flex items-center'>
                <Checkbox
                  checked={accessibilities.includes(value)}
                  cssClass='rp-checkbox-input'
                  id={label}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    handleSelectAccessibilities(e.target.checked, value)
                  }
                />
                <Label className='font-black3 text-sm' htmlFor={label} text={label} />
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className=''>
        <div className='text-center'>
          <Label htmlFor={trips.specialInstructions} text={trips.specialInstructions} />
        </div>
        <div className='mt03'>
          <Textarea
            {...PORTAL_INPUT_PROPS}
            placeholder={placeholder.anySpecialInstructions}
            value={specialInstructions}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleSpecialInstructionsChange(e.target.value)}
          />
        </div>
      </div>
    </>
  )
}

export default TripDetails



// WEBPACK FOOTER //
// ./src/components/riders/rider-portal/trips/create/Step2/TripDetails.tsx